import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Snackbar from '@mui/material/Snackbar';
import DiamondIcon from '@mui/icons-material/Diamond';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import PageShell from '../components/PageShell';
import CustomerNav from '../components/CustomerNav';
import request from '../api';
import { REPUTATION_COLORS, reputationProgress } from '../reputationUtils';

function ReputationCard({ reputation }) {
    const { points, level, levelLabel, nextLevelThreshold, salesNeededForNextLevel, nextResetAt } = reputation;
    const progress = reputationProgress(reputation);
    const resetDate = new Date(nextResetAt).toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
    });

    return (
        <Paper sx={{ p: { xs: 2.5, sm: 3 }, mb: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                <Typography variant="h6">Reputación</Typography>
                <Chip
                    icon={<WorkspacePremiumIcon />}
                    label={levelLabel}
                    color={level ? REPUTATION_COLORS[level] : 'default'}
                />
            </Stack>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {points} pts
            </Typography>
            <LinearProgress
                variant="determinate"
                value={progress}
                color={level ? REPUTATION_COLORS[level] : 'info'}
                sx={{ height: 8, borderRadius: 4, mb: 1.5 }}
            />
            <Typography variant="body2" color="text.secondary">
                {nextLevelThreshold
                    ? `Completa ${salesNeededForNextLevel} ventas más para subir de nivel.`
                    : '¡Has alcanzado el nivel máximo!'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Tu reputación se reinicia el {resetDate}. Si mantienes un nivel, conservas el mínimo de puntos necesario para él.
            </Typography>
        </Paper>
    );
}

function MyProfile() {
    const [token] = useState(localStorage.getItem('customerToken'));
    const [customer, setCustomer] = useState(null);
    const [snackbar, setSnackbar] = useState('');
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [emailPassword, setEmailPassword] = useState('');
    const [emailBusy, setEmailBusy] = useState(false);
    const [emailError, setEmailError] = useState('');

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordBusy, setPasswordBusy] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    const loadMe = useCallback(() => {
        if (!token) return;
        request('/customer/me', { token })
            .then((cust) => {
                setCustomer(cust);
                setEmail(cust.email || '');
            })
            .catch(() => {
                localStorage.removeItem('customerToken');
                navigate('/auctions');
            });
    }, [token, navigate]);

    useEffect(() => {
        if (!token) {
            navigate('/auctions');
            return;
        }
        loadMe();
    }, [token, navigate, loadMe]);

    const submitEmail = async (e) => {
        e.preventDefault();
        setEmailError('');
        setEmailBusy(true);
        try {
            const updated = await request('/customer/email', {
                method: 'PATCH',
                token,
                body: { email, current_password: emailPassword },
            });
            setCustomer(updated);
            setEmailPassword('');
            setSnackbar('Correo actualizado');
        } catch (err) {
            setEmailError(err.message);
        } finally {
            setEmailBusy(false);
        }
    };

    const submitPassword = async (e) => {
        e.preventDefault();
        setPasswordError('');
        if (newPassword !== confirmPassword) {
            setPasswordError('La nueva contraseña y su confirmación no coinciden');
            return;
        }
        setPasswordBusy(true);
        try {
            await request('/customer/password', {
                method: 'PATCH',
                token,
                body: { current_password: currentPassword, new_password: newPassword },
            });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setSnackbar('Contraseña actualizada');
        } catch (err) {
            setPasswordError(err.message);
        } finally {
            setPasswordBusy(false);
        }
    };

    if (!customer) {
        return (
            <PageShell title="Mi Perfil" nav={<CustomerNav />}>
                <Typography color="text.secondary">Cargando...</Typography>
            </PageShell>
        );
    }

    return (
        <PageShell title="Mi Perfil" nav={<CustomerNav />} maxWidth="md">
            <Stack alignItems="center" spacing={1.5} sx={{ mb: 5 }}>
                <Avatar
                    src={customer.equipped_avatar_url || undefined}
                    sx={{ width: 80, height: 80, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '2rem' }}
                >
                    {!customer.equipped_avatar_url && customer.username.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="h5" fontWeight={700}>{customer.username}</Typography>
                <Chip
                    icon={<DiamondIcon />}
                    label={`${Number(customer.gems).toFixed(0)} gemas`}
                    color="secondary"
                    variant="outlined"
                />
                <Button component={RouterLink} to="/gem-store" size="small">
                    Comprar avatares
                </Button>
            </Stack>

            {customer.reputation && <ReputationCard reputation={customer.reputation} />}

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: { xs: 2.5, sm: 3 }, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Cambiar Correo</Typography>
                        <Box component="form" onSubmit={submitEmail}>
                            <Stack spacing={2}>
                                <TextField
                                    label="Correo electrónico"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    fullWidth
                                    required
                                />
                                <TextField
                                    label="Contraseña Actual"
                                    type="password"
                                    value={emailPassword}
                                    onChange={(e) => setEmailPassword(e.target.value)}
                                    fullWidth
                                    required
                                />
                                <Button type="submit" variant="contained" size="large" disabled={emailBusy} fullWidth>
                                    Actualizar Correo
                                </Button>
                                {emailError && <Alert severity="error">{emailError}</Alert>}
                            </Stack>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: { xs: 2.5, sm: 3 }, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Cambiar Contraseña</Typography>
                        <Box component="form" onSubmit={submitPassword}>
                            <Stack spacing={2}>
                                <TextField
                                    label="Contraseña Actual"
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    fullWidth
                                    required
                                />
                                <TextField
                                    label="Nueva Contraseña"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    fullWidth
                                    required
                                />
                                <TextField
                                    label="Confirmar Nueva Contraseña"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    fullWidth
                                    required
                                />
                                <Button type="submit" variant="contained" size="large" disabled={passwordBusy} fullWidth>
                                    Actualizar Contraseña
                                </Button>
                                {passwordError && <Alert severity="error">{passwordError}</Alert>}
                            </Stack>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <Snackbar
                open={!!snackbar}
                autoHideDuration={4000}
                onClose={() => setSnackbar('')}
                message={snackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </PageShell>
    );
}

export default MyProfile;
